import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { colors } from './theme';

// Pages
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import HomePage from './pages/HomePage';
import BookingPage from './pages/BookingPage';
import JobStatusPage from './pages/JobStatusPage';
import ReviewPage from './pages/ReviewPage';
import MyJobsPage from './pages/MyJobsPage';
import ProfilePage from './pages/ProfilePage';
import WrongRolePage from './pages/WrongRolePage';

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <img src="/logo-white.png" alt="SageTech" style={{ height: 48, marginBottom: 24, objectFit: 'contain' }} />
      <div
        style={{
          width: 32,
          height: 32,
          border: `3px solid rgba(102,51,153,0.3)`,
          borderTopColor: colors.primary,
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'technician') return <Navigate to="/wrong-role" replace />;

  return <>{children}</>;
}

function AppRoutes() {
  const { user, profile, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      {/* Root redirect */}
      <Route
        path="/"
        element={
          user
            ? profile?.role === 'technician'
              ? <Navigate to="/wrong-role" replace />
              : <Navigate to="/home" replace />
            : <Navigate to="/login" replace />
        }
      />

      {/* Public */}
      <Route path="/login" element={user ? <Navigate to="/home" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/home" replace /> : <SignUpPage />} />
      <Route path="/wrong-role" element={<WrongRolePage />} />

      {/* Protected */}
      <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/booking" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
      <Route path="/job/:jobId" element={<ProtectedRoute><JobStatusPage /></ProtectedRoute>} />
      <Route path="/review/:jobId" element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
      <Route path="/my-jobs" element={<ProtectedRoute><MyJobsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div
          style={{
            maxWidth: 480,
            margin: '0 auto',
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
